import { useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'pcgop.field-mode';

function applyToDocument(on: boolean) {
  if (typeof document === 'undefined') return;
  if (on) document.documentElement.setAttribute('data-field-mode', '1');
  else document.documentElement.removeAttribute('data-field-mode');
}

function readStored(): boolean | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === '1') return true;
    if (v === '0') return false;
    return null;
  } catch {
    return null;
  }
}

function writeStored(on: boolean) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
  } catch {
    // ignore — Safari private mode etc.
  }
}

/**
 * Returns [enabled, setEnabled]. Defaults to `defaultOn` when nothing stored.
 * On mount, applies the stored (or default) value to document.documentElement.
 */
export function useFieldMode(defaultOn: boolean = false): [boolean, (on: boolean) => void] {
  const [enabled, setEnabledState] = useState<boolean>(() => {
    const stored = readStored();
    return stored !== null ? stored : defaultOn;
  });

  useEffect(() => {
    applyToDocument(enabled);
  }, [enabled]);

  const setEnabled = useCallback((on: boolean) => {
    writeStored(on);
    setEnabledState(on);
  }, []);

  return [enabled, setEnabled];
}
